import { XHRAuthorizedClient } from '@/remote/client';
import {
  useAddSiteManager,
  useRemoveSiteManager,
} from '@/remote/site-management/site-managers';
import { useUsersQuery } from '@/remote/user-management/fetch-users';
import type { ApiResponse, CreateSiteData, Site, User } from '@/types/site';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

interface NotificationSettingsTabProps {
  isEditMode?: boolean;
}

export default function NotificationSettingsTab({
  isEditMode = false,
}: NotificationSettingsTabProps) {
  const {
    control,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<Site | CreateSiteData>();

  const siteId = watch('siteId');
  const currentManagerIds = watch('notificationManagerIds') || [];

  // 전체 사용자 목록 (관리자 추가용)
  const { data: allUsers = [] } = useUsersQuery();

  // 사이트에 할당된 관리자 목록 (편집 모드에서만)
  const { data: siteManagers = [], refetch: refetchManagers } = useQuery({
    queryKey: ['siteManagers', siteId, isEditMode],
    queryFn: async (): Promise<User[]> => {
      if (!isEditMode || !siteId) return [];
      const response = await XHRAuthorizedClient.get<ApiResponse<User[]>>(
        `/sites/${siteId}/managers`
      );
      return response.data.data;
    },
    enabled: isEditMode && Boolean(siteId),
  });

  const addManagerMutation = useAddSiteManager();
  const removeManagerMutation = useRemoveSiteManager();

  const [showManagerDropdown, setShowManagerDropdown] = useState(false);
  const [selectedManagerToAdd, setSelectedManagerToAdd] = useState<string>('');

  // 사이트에 할당되지 않은 관리자 필터링
  const availableManagers = allUsers.filter(
    (user) => !siteManagers.some((manager) => manager.id === user.id)
  );

  // 알림 관리자 체크박스 토글
  const handleNotificationToggle = (managerId: string) => {
    const current = currentManagerIds || [];
    const newIds = current.includes(managerId)
      ? current.filter((id) => id !== managerId)
      : [...current, managerId];

    setValue('notificationManagerIds', newIds, {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  // 사이트에 관리자 추가
  const handleAddManager = async () => {
    if (!selectedManagerToAdd || !siteId || !isEditMode) return;

    try {
      await addManagerMutation.mutateAsync({
        siteId,
        managerId: selectedManagerToAdd,
      });
      await refetchManagers();
      setSelectedManagerToAdd('');
      setShowManagerDropdown(false);
      alert('관리자가 성공적으로 추가되었습니다.');
    } catch (error) {
      console.error('관리자 추가 실패:', error);
      alert('관리자 추가에 실패했습니다.');
    }
  };

  // 사이트에서 관리자 제거
  const handleRemoveManager = async (managerId: string) => {
    if (!siteId || !isEditMode) return;

    if (!confirm('이 관리자를 사이트에서 제거하시겠습니까?')) return;

    try {
      await removeManagerMutation.mutateAsync({
        siteId,
        managerId,
      });

      // 알림 관리자 목록에서도 제거
      const newNotificationIds = (currentManagerIds || []).filter(
        (id) => id !== managerId
      );
      setValue('notificationManagerIds', newNotificationIds, {
        shouldValidate: true,
        shouldDirty: true,
      });

      await refetchManagers();
      alert('관리자가 성공적으로 제거되었습니다.');
    } catch (error) {
      console.error('관리자 제거 실패:', error);
      alert('관리자 제거에 실패했습니다.');
    }
  };

  return (
    <div className="space-y-8">
      {/* 수위 설정 섹션 */}
      <div className="border-b pb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">수위 알림 설정</h3>
          <Controller
            name="waterLevelSmsEnabled"
            control={control}
            defaultValue={true}
            render={({ field }) => (
              <button
                type="button"
                onClick={() => field.onChange(!field.value)}
                className={clsx(
                  'relative inline-flex items-center h-6 w-11 rounded-full transition-colors',
                  field.value !== false ? 'bg-blue-600' : 'bg-gray-300'
                )}
              >
                <span
                  className={clsx(
                    'inline-block h-4 w-4 transform rounded-full bg-white transition-transform',
                    field.value !== false ? 'translate-x-6' : 'translate-x-1'
                  )}
                />
              </button>
            )}
          />
        </div>
        <p className="text-sm text-gray-600 mb-4">
          수위가 설정한 범위를 벗어나면 SMS 알림을 발송합니다.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* 저수위 설정 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              저수위 (%)
            </label>
            <Controller
              name="lowWaterLevel"
              control={control}
              defaultValue={40}
              render={({ field }) => (
                <div className="space-y-2">
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={field.value || 40}
                    onChange={(e) => field.onChange(Number(e.target.value))}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                  />
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={field.value || 40}
                      onChange={(e) => field.onChange(Number(e.target.value))}
                      className="w-24 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-600">%</span>
                  </div>
                  <p className="text-xs text-gray-500">
                    수위가 이 값 이하로 떨어지면 알림을 발송합니다.
                  </p>
                </div>
              )}
            />
            {errors.lowWaterLevel && (
              <p className="mt-1 text-sm text-red-600">
                {errors.lowWaterLevel.message}
              </p>
            )}
          </div>

          {/* 고수위 설정 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              고수위 (%)
            </label>
            <Controller
              name="highWaterLevel"
              control={control}
              defaultValue={100}
              render={({ field }) => (
                <div className="space-y-2">
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={field.value || 100}
                    onChange={(e) => field.onChange(Number(e.target.value))}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                  />
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={field.value || 100}
                      onChange={(e) => field.onChange(Number(e.target.value))}
                      className="w-24 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-600">%</span>
                  </div>
                  <p className="text-xs text-gray-500">
                    수위가 이 값 이상으로 올라가면 알림을 발송합니다.
                  </p>
                </div>
              )}
            />
            {errors.highWaterLevel && (
              <p className="mt-1 text-sm text-red-600">
                {errors.highWaterLevel.message}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* 에러 상황별 SMS 알림 설정 섹션 */}
      <div className="border-b pb-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          이상이 감지되었을 때 SMS 알림 설정
        </h3>
        <p className="text-sm text-gray-600 mb-4">
          이상이 감지되었을 때 SMS 알림을 받을지 설정합니다.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* 전파 이상 */}
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <label className="text-sm font-medium text-gray-700">
              전파 이상
            </label>
            <Controller
              name="electricWaveErrorSmsEnabled"
              control={control}
              defaultValue={false}
              render={({ field }) => (
                <button
                  type="button"
                  onClick={() => field.onChange(!field.value)}
                  className={clsx(
                    'relative inline-flex h-6 w-11 items-center rounded-full transition-colors',
                    field.value ? 'bg-blue-600' : 'bg-gray-300'
                  )}
                >
                  <span
                    className={clsx(
                      'inline-block h-4 w-4 transform rounded-full bg-white transition-transform',
                      field.value ? 'translate-x-6' : 'translate-x-1'
                    )}
                  />
                </button>
              )}
            />
          </div>

          {/* 과부하 이상 */}
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <label className="text-sm font-medium text-gray-700">
              과부하 이상
            </label>
            <Controller
              name="overloadErrorSmsEnabled"
              control={control}
              defaultValue={false}
              render={({ field }) => (
                <button
                  type="button"
                  onClick={() => field.onChange(!field.value)}
                  className={clsx(
                    'relative inline-flex h-6 w-11 items-center rounded-full transition-colors',
                    field.value ? 'bg-blue-600' : 'bg-gray-300'
                  )}
                >
                  <span
                    className={clsx(
                      'inline-block h-4 w-4 transform rounded-full bg-white transition-transform',
                      field.value ? 'translate-x-6' : 'translate-x-1'
                    )}
                  />
                </button>
              )}
            />
          </div>

          {/* 지하수 이상 */}
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <label className="text-sm font-medium text-gray-700">
              지하수 이상
            </label>
            <Controller
              name="undergroundWaterErrorSmsEnabled"
              control={control}
              defaultValue={false}
              render={({ field }) => (
                <button
                  type="button"
                  onClick={() => field.onChange(!field.value)}
                  className={clsx(
                    'relative inline-flex h-6 w-11 items-center rounded-full transition-colors',
                    field.value ? 'bg-blue-600' : 'bg-gray-300'
                  )}
                >
                  <span
                    className={clsx(
                      'inline-block h-4 w-4 transform rounded-full bg-white transition-transform',
                      field.value ? 'translate-x-6' : 'translate-x-1'
                    )}
                  />
                </button>
              )}
            />
          </div>
        </div>
      </div>

      {/* 알림 수신 관리자 설정 섹션 */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">
            알림 수신 관리자
          </h3>
          {isEditMode && (
            <button
              type="button"
              onClick={() => setShowManagerDropdown(!showManagerDropdown)}
              className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              + 관리자 추가
            </button>
          )}
        </div>

        {/* 관리자 추가 드롭다운 */}
        {isEditMode && showManagerDropdown && (
          <div className="mb-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
            <div className="flex items-center gap-2">
              <select
                value={selectedManagerToAdd}
                onChange={(e) => setSelectedManagerToAdd(e.target.value)}
                className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
              >
                <option value="">관리자를 선택하세요</option>
                {availableManagers.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.name} ({user.loginId})
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={handleAddManager}
                disabled={!selectedManagerToAdd || addManagerMutation.isPending}
                className="px-4 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {addManagerMutation.isPending ? '추가 중...' : '추가'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowManagerDropdown(false);
                  setSelectedManagerToAdd('');
                }}
                className="px-4 py-2 bg-gray-600 text-white text-sm rounded-lg hover:bg-gray-700 transition-colors"
              >
                취소
              </button>
            </div>
          </div>
        )}

        <p className="text-sm text-gray-600 mb-4">
          {isEditMode
            ? '사이트에 할당된 관리자 중 수위 알림을 받을 관리자를 선택하세요.'
            : '생성 모드에서는 사이트 생성 후 관리자를 추가하고 알림 설정을 할 수 있습니다.'}
          <br />
          SMS 수신 동의를 하지 않은 관리자는 수위 알림을 받을 수 없습니다.
        </p>

        {/* 관리자 목록 */}
        {siteManagers.length === 0 ? (
          <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-200">
            <p className="text-gray-500 text-sm">
              {isEditMode
                ? '할당된 관리자가 없습니다. 관리자를 추가해주세요.'
                : '사이트 생성 후 관리자를 추가할 수 있습니다.'}
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {siteManagers.map((manager) => (
              <div
                key={manager.id}
                className="flex items-center justify-between p-4 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center gap-4 flex-1">
                  <input
                    type="checkbox"
                    checked={currentManagerIds.includes(manager.id)}
                    onChange={() => handleNotificationToggle(manager.id)}
                    className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">{manager.name}</p>
                    <div className="flex items-center gap-3 mt-1">
                      <p className="text-sm text-gray-600">
                        ID: {manager.loginId}
                      </p>
                      <p className="text-sm text-gray-600">
                        연락처: {manager.mobilePhone}
                      </p>

                      {
                        <span
                          className={clsx(
                            'text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded',
                            manager.smsReceive
                              ? 'bg-green-100 text-green-700'
                              : 'bg-red-100 text-red-700'
                          )}
                        >
                          SMS 수신 동의 :{' '}
                          {manager.smsReceive ? '동의' : '미동의'}
                        </span>
                      }
                    </div>
                  </div>
                </div>
                {isEditMode && (
                  <button
                    type="button"
                    onClick={() => handleRemoveManager(manager.id)}
                    disabled={removeManagerMutation.isPending}
                    className="ml-4 px-3 py-1.5 text-sm text-red-600 border border-red-300 rounded-lg hover:bg-red-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    제거
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {/* 알림 수신 관리자 요약 */}
        {currentManagerIds.length > 0 && (
          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-800">
              <strong>{currentManagerIds.length}명</strong>의 관리자가 수위
              알림을 받습니다.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
